// src/pages/Dashboard.jsx
import React, { useContext } from 'react';
import Header from '../components/common/Header';
import Sidebar from '../components/common/Sidebar';
import { useUser } from '../context/UserContext';
import { useAuth } from '../context/AuthContext';
import { ThemeContext } from '../context/ThemeContext';
import StatsCard from '../components/dashboard/StatsCard';
import NotesLists from '../components/dashboard/NotesLists';
import QuickActions from '../components/dashboard/QuickActions';
import ZoomImport from '../components/dashboard/ZoomImport';
import AdBanner from '../components/common/AdBanner';
import RecentFlashcards from '../components/dashboard/RecentFlashcards';
import { useEffect, useState } from 'react';
import userService from '../services/userService';
import paymentService from '../services/paymentService';
import { Link, useNavigate } from 'react-router-dom';
import { FileText, Brain, Zap, Database } from 'lucide-react';

const Dashboard = () => {
  const { user, userType } = useUser();
  const { logout } = useAuth();
  const { refreshUser } = useUser();
  const { theme } = useContext(ThemeContext);
  const navigate = useNavigate();

  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [cancelling, setCancelling] = useState(false);

  const handleQuickAction = (actionId) => {
    switch (actionId) {
      case 'create-flashcard':
      case 'ai-generate':
        navigate('/flashcards');
        break;
      case 'quick-study':
      case 'quick-review':
        navigate('/flashcards');
        break;
      case 'import-export':
      case 'create-deck':
        navigate('/flashcards');
        break;
      case 'search-cards':
        navigate('/search');
        break;
      case 'stats':
        // Already on dashboard with stats visible
        window.scrollTo({ top: 0, behavior: 'smooth' });
        break;
      case 'settings':
        navigate('/settings');
        break;
      case 'share-deck':
      case 'ai-analyze':
      case 'community':
        alert('This feature is coming soon!');
        break;
      default:
        break;
    }
  };

  useEffect(() => {
    const fetchDashboard = async () => {
      try {
        const res = await userService.getDashboard();
        setDashboard(res.data || res);
      } catch (err) {
        console.error('Failed to fetch dashboard', err);
      } finally {
        setLoading(false);
      }
    };

    fetchDashboard();
  }, []);

  // Stats data - use only server data, no fallbacks to cached user data
  const stats = [
    { title: 'Total Notes', value: dashboard?.stats?.notesCount ?? 0, icon: <FileText size={26} /> },
    { title: 'Flashcards', value: dashboard?.stats?.flashcardsCount ?? 0, icon: <Brain size={26} /> },
    { title: 'AI Searches Used', value: `${dashboard?.stats?.aiSearchesUsed ?? 0}/${dashboard?.stats?.aiSearchLimit ?? 5}`, icon: <Zap size={26} /> },
    { title: 'Storage Used', value: `${dashboard?.stats?.storageUsed ?? 0} MB`, icon: <Database size={26} /> },
  ];

  return (
    <div className={`dashboard ${theme === 'dark' ? 'dark' : ''}`}>
      <Header />
      <div className="flex">
        <Sidebar />
        <main className="flex-1 p-4 sm:p-6 w-full overflow-x-hidden">
          {/* Welcome message */}
          <div className="mb-8 flex flex-col sm:flex-row items-start justify-between gap-4">
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white">
                Welcome back, {user?.name || 'User'}!
              </h1>
              <p className="text-sm sm:text-base text-gray-600 dark:text-gray-300 mt-2">
                {userType === 'pro' ? 'Pro Member' : 'Free Plan'} • Last login: {user?.lastLogin ? new Date(user.lastLogin).toLocaleString() : 'Never'}
              </p>
            </div>
            <div>
              <button
                onClick={async () => {
                  try { await logout(); } catch (e) { /* ignore */ }
                  try { await refreshUser(); } catch (e) { /* ignore */ }
                  window.location.href = '/';
                }}
                className="bg-red-50 text-red-600 px-3 py-2 rounded-md hover:bg-red-100 w-full sm:w-auto"
              >
                Logout
              </button>
            </div>
          </div>

          {/* Show ads for free users */}
          {userType === 'free' && <AdBanner />}

          {/* Top KPI Cards */}
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-6 mb-8">
            {stats.map(s => (
              <div key={s.title} className="bg-white dark:bg-gray-800 rounded-xl shadow p-4 flex items-center gap-4">
                <div className="p-3 rounded-lg bg-gray-50 dark:bg-gray-900/20 flex items-center justify-center" style={{ minWidth: 56, minHeight: 56 }}>
                  <div className="text-indigo-600 dark:text-indigo-300">{s.icon}</div>
                </div>
                <div>
                  <div className="text-sm text-gray-500 dark:text-gray-400">{s.title}</div>
                  <div className="text-xl font-bold text-gray-900 dark:text-white">{s.value}</div>
                </div>
              </div>
            ))}
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Left Column - Notes & Quick Actions */}
            <div className="lg:col-span-2 space-y-8">
              <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg p-6">
                <div className="flex justify-between items-center mb-6">
                  <h2 className="text-xl font-bold text-gray-900 dark:text-white">Recent Notes</h2>
                  <button className="text-blue-600 dark:text-blue-400 hover:underline">
                    View All
                  </button>
                </div>
                <NotesLists recentNotes={dashboard?.recentNotes} loading={loading} />

                <div className="mt-6">
                  <h3 className="text-lg font-bold mb-3">Recent Flashcards</h3>
                  <RecentFlashcards recentFlashcards={dashboard?.recentFlashcards} loading={loading} />
                </div>
                {/* Detailed study analytics card */}
                <div className="mt-6">
                  <StatsCard stats={dashboard?.stats} user={user} />
                </div>
              </div>

              <QuickActions onAction={handleQuickAction} />
            </div>

            {/* Right Column - Features */}
            <div className="space-y-8">
              {userType === 'pro' && (
                <div className="mt-6">
                  <ZoomImport />
                </div>
              )}

              {/* Subscription & Payments */}
              <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg p-6 mt-6">
                <h3 className="text-lg font-bold mb-4">Subscription</h3>
                {loading ? (
                  <p className="text-sm text-gray-500">Loading...</p>
                ) : (
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-gray-500">Plan</p>
                        <p className="font-semibold text-gray-900 dark:text-white">{dashboard?.stats?.plan || user?.plan || 'free'}</p>
                        {dashboard?.stats?.subscriptionEndsAt && (
                          <p className="text-sm text-gray-500">Next billing date: {new Date(dashboard.stats.subscriptionEndsAt).toLocaleDateString()}</p>
                        )}
                      </div>
                      <div className="text-right">
                        <p className={`inline-block px-3 py-2 rounded ${dashboard?.stats?.subscriptionStatus === 'active' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-700'}`}>{dashboard?.stats?.subscriptionStatus || 'trialing'}</p>
                      </div>
                    </div>

                    {dashboard?.lastTransaction ? (
                      <div className="p-4 bg-gray-50 dark:bg-gray-700 rounded">
                        <p className="text-sm text-gray-500">Last payment</p>
                        <p className="font-medium">{dashboard.lastTransaction.reference} • {dashboard.lastTransaction.amount} {dashboard.lastTransaction.currency}</p>
                        <p className="text-sm text-gray-500">Status: {dashboard.lastTransaction.status} • Date: {dashboard.lastTransaction.paidAt ? new Date(dashboard.lastTransaction.paidAt).toLocaleString() : 'N/A'}</p>
                      </div>
                    ) : (
                      <p className="text-sm text-gray-500">
                        No recent payments. <Link to="/profile#billing" className="text-blue-600 hover:underline">View billing history</Link>
                      </p>
                    )}

                    {dashboard?.stats?.subscriptionId && dashboard?.stats?.subscriptionStatus === 'active' && (
                      <div className="flex gap-3">
                        <button
                          className="py-2 px-4 bg-red-600 text-white rounded hover:bg-red-700"
                          onClick={async () => {
                            if (!window.confirm('Are you sure you want to cancel your subscription?')) return;
                            setCancelling(true);
                            try {
                              const res = await paymentService.cancel(dashboard.stats.subscriptionId);
                              if (res.status === 'success' || res.data?.status) {
                                alert('Your subscription has been cancelled successfully.');
                                await refreshUser();
                                const fresh = await userService.getDashboard();
                                setDashboard(fresh.data || fresh);
                              } else {
                                alert(res.message || 'Unable to cancel subscription. Please try again or contact support.');
                              }
                            } catch (err) {
                              console.error(err);
                              alert(err.userMessage || err.response?.data?.message || 'Unable to cancel subscription. Please contact support.');
                            } finally {
                              setCancelling(false);
                            }
                          }}
                          disabled={cancelling}
                        >
                          {cancelling ? 'Cancelling...' : 'Cancel Subscription'}
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </div>
              
              {/* Pro Features Showcase */}
              {userType === 'free' && (
                <div className="bg-gradient-to-r from-purple-500 to-pink-500 rounded-xl shadow-lg p-6 text-white">
                  <h3 className="text-xl font-bold mb-4">Upgrade to Pro</h3>
                  <ul className="space-y-3 mb-6">
                    <li className="flex items-center">
                      <span className="mr-2">✓</span> Unlimited AI Searches
                    </li>
                    <li className="flex items-center">
                      <span className="mr-2">✓</span> Zoom Integration
                    </li>
                    <li className="flex items-center">
                      <span className="mr-2">✓</span> No Ads
                    </li>
                    <li className="flex items-center">
                      <span className="mr-2">✓</span> Advanced Analytics
                    </li>
                  </ul>
                  <a href="/checkout" className="w-full inline-block text-center bg-white text-purple-600 font-bold py-3 rounded-lg hover:bg-gray-100 transition">Upgrade Now</a>
                </div>
              )}
            </div>
          </div>
        </main>
      </div>
    </div>
  );
};

export default Dashboard;